import { FocusEventHandler, memo, useCallback, useContext, useEffect, useId, useState } from "react";
import { TextField, TextFieldProps } from "../../../form";
import { SpreadsheetHighlightContext } from "../SpreadsheetHighlightContext";

import styles from "./style.module.scss";

export type SpreadsheetColumnPickerProps = TextFieldProps & {
  highlightColor?: string,
  highlightAlways?: boolean
  highlightOnFocus?: boolean
}

export const SpreadsheetColumnPicker = memo<SpreadsheetColumnPickerProps>((
  {
    highlightColor,
    value,
    children,
    onFocus,
    onBlur,
    highlightAlways = true,
    highlightOnFocus = false,
    ...props
  }) => {
  const { registerColumn } = useContext(SpreadsheetHighlightContext);
  const id = useId();
  const [ isFocused, setIsFocused ] = useState(false);

  useEffect(() => {
    if (highlightColor && (highlightAlways || (highlightOnFocus && isFocused))) {
      registerColumn(id, value, highlightColor);

      return () => {
        registerColumn(id, null, highlightColor);
      };
    }
  }, [ id, value, highlightColor, highlightAlways, highlightOnFocus, isFocused, registerColumn ]);

  const wrappedOnBlur: FocusEventHandler<HTMLInputElement> = useCallback(event => {
    setIsFocused(false);
    onBlur?.(event);
  }, [ onBlur ]);

  const wrappedOnFocus: FocusEventHandler<HTMLInputElement> = useCallback(event => {
    setIsFocused(true);
    onFocus?.(event);
  }, [ onFocus ]);

  return (
    <TextField
      value={value}
      isClearable={false}
      onBlur={wrappedOnBlur}
      onFocus={wrappedOnFocus}
      {...props}
    >
      <div className={styles.marker} style={{ background: highlightColor }} />
      {children}
    </TextField>
  );
});

export default SpreadsheetColumnPicker;
